import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useContent } from '../context/ContentContext';
import { ChevronRight, MessageSquare, ClipboardCheck, Users, FileCheck, CheckCircle, HelpCircle, Zap, ShieldCheck, Briefcase } from 'lucide-react';

const getIcon = (name: string) => {
  switch (name) {
    case 'MessageSquare': return MessageSquare;
    case 'ClipboardCheck': return ClipboardCheck;
    case 'Users': return Users;
    case 'FileCheck': return FileCheck;
    case 'CheckCircle': return CheckCircle;
    case 'Zap': return Zap;
    case 'ShieldCheck': return ShieldCheck;
    case 'Briefcase': return Briefcase;
    default: return HelpCircle;
  }
};

const Process: React.FC = () => {
  const { content } = useContent();
  const processSteps = content.process || [];
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="process" ref={sectionRef} className="py-24 bg-slate-50 overflow-hidden relative">
      {/* 视差背景图 */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <img 
          src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=1920" 
          alt="Process Background" 
          className="w-full h-[120%] object-cover opacity-[0.03] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-transparent to-slate-50"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">专业服务流程</h2>
          <p className="text-[#D4AF37] tracking-widest font-semibold text-sm">SERVICE PROCESS</p>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mt-4 rounded-full"></div>
        </motion.div>

        <div className="relative">
          {/* Connecting Line (Desktop) - Enhanced with dashed style */}
          <div className="hidden lg:block absolute top-[3.5rem] left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-gray-300 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 relative z-10">
            {processSteps.map((step, index) => {
              const Icon = getIcon(step.iconName);
              return (
                <motion.div 
                  key={step.id || index} 
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  className="relative group"
                >
                  <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-[#D4AF37]/30 transition-all duration-300 h-full flex flex-col items-center text-center group-hover:-translate-y-2">
                    
                    {/* Icon Container */}
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 relative z-10 shadow-md group-hover:shadow-[#D4AF37]/20 transition-all border-4 border-slate-50 group-hover:border-[#D4AF37]/10">
                      <div className="absolute inset-0 bg-[#D4AF37] opacity-0 group-hover:opacity-100 rounded-full transition-opacity duration-300"></div>
                      <Icon size={32} className="text-slate-700 group-hover:text-white transition-colors relative z-10" />
                      
                      {/* Step Number Badge */}
                      <div className="absolute -top-1 -right-1 w-7 h-7 bg-slate-800 text-[#D4AF37] rounded-full flex items-center justify-center text-xs font-bold border-2 border-white shadow-sm">
                        {step.id}
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-slate-800 mb-3 group-hover:text-[#D4AF37] transition-colors">{step.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed group-hover:text-gray-600">{step.description}</p>
                  </div>
                  
                  {/* Mobile Arrow */}
                  {index < processSteps.length - 1 && (
                    <div className="lg:hidden flex justify-center py-4 text-gray-300">
                      <ChevronRight size={24} className="rotate-90 animate-bounce" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;